import { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import QuranReader from '../components/quran/QuranReader';
import SurahList from '../components/quran/SurahList';
import { useAuth } from '../context/AuthContext';
import { surahs } from '../data/quranData';
import Footer from '../components/layout/Footer';

const { FiBook, FiList } = FiIcons;

const Quran = () => {
    const { user } = useAuth();
    const [selectedSurah, setSelectedSurah] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [selectedPlaylistId, setSelectedPlaylistId] = useState('all');

    const playlists = user?.playlists || [];

    const handlePlaylistChange = (e) => {
        setSelectedPlaylistId(e.target.value);
        setSelectedSurah(null); // Reset selection when playlist changes
        setIsPlaying(false);
    };

    const getFilterSurahIds = () => {
        if (selectedPlaylistId === 'all') return null;
        const playlist = playlists.find(p => p.id === selectedPlaylistId);
        return playlist ? playlist.surahs : [];
    };

    const handleSurahEnded = () => {
        if (!selectedSurah) return;

        let surahListIds = [];

        if (selectedPlaylistId === 'all') {
            surahListIds = surahs.map(s => s.number);
        } else {
            const playlist = playlists.find(p => p.id === selectedPlaylistId);
            if (playlist) {
                surahListIds = playlist.surahs;
            }
        }

        const currentIndex = surahListIds.indexOf(selectedSurah.number);

        // If there is a next surah in the list/playlist
        if (currentIndex !== -1 && currentIndex < surahListIds.length - 1) {
            const nextSurahId = surahListIds[currentIndex + 1];
            const nextSurah = surahs.find(s => s.number === nextSurahId);

            if (nextSurah) {
                setSelectedSurah(nextSurah);
                // We keep isPlaying as true so the next one starts automatically
            }
        } else {
            // End of playlist
            setIsPlaying(false);
        }
    };

    return (
        <div className="min-h-screen lg:h-screen w-full flex flex-col">
            <div className="flex-1 flex flex-col max-w-[1600px] mx-auto w-full p-4 md:p-6 lg:h-full">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="shrink-0 mb-4 md:mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full md:w-auto">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <SafeIcon icon={FiBook} className="text-emerald-600" />
                            Holy Quran
                        </h1>

                        {/* Playlist Dropdown */}
                        {user && (
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <SafeIcon icon={FiList} className="text-gray-500 dark:text-gray-400 shrink-0" />
                                <select
                                    value={selectedPlaylistId}
                                    onChange={handlePlaylistChange}
                                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded-lg focus:ring-emerald-500 focus:border-emerald-500 block w-full sm:w-auto p-2 outline-none"
                                >
                                    <option value="all">All Surahs</option>
                                    {playlists.map(playlist => (
                                        <option key={playlist.id} value={playlist.id}>
                                            {playlist.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Content Area - Flex/Grid to fill remaining space */}
                <div className="flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-12 gap-4 lg:gap-6">
                    {/* Surah List Sidebar */}
                    <div className="lg:col-span-3 h-auto lg:h-full order-2 lg:order-1 flex flex-col overflow-hidden">
                        <SurahList
                            onSurahSelect={(surah) => {
                                setSelectedSurah(surah);
                                // Scroll to top of reader on mobile when selected
                                if (window.innerWidth < 1024) {
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                            }}
                            selectedSurah={selectedSurah}
                            filterSurahIds={getFilterSurahIds()}
                        />
                    </div>

                    {/* Main Reading Area */}
                    <div className="lg:col-span-9 h-[600px] lg:h-full order-1 lg:order-2 flex flex-col overflow-hidden">
                        {selectedSurah ? (
                            <QuranReader
                                surah={selectedSurah}
                                isPlaying={isPlaying}
                                onPlayToggle={() => setIsPlaying(!isPlaying)}
                                onSurahEnded={handleSurahEnded}
                            />
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700 h-full flex flex-col items-center justify-center min-h-[300px]"
                            >
                                <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mb-6">
                                    <SafeIcon icon={FiBook} className="w-10 h-10 text-emerald-600" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                                    Select a Surah
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300 max-w-md">
                                    Choose a Surah from the list to begin reading, listening, and reflecting on the Holy Quran.
                                </p>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Quran;